/**
 * Subir la orden de compra del cliente (PDF) y convertirla en líneas del pedido.
 *
 * El servidor lee el PDF, traduce cada línea a referencia + extensión de SIESA y
 * marca lo que no cuadra. Aquí solo se muestra para revisar y, con el pedido ya
 * creado, se agregan las líneas listas con el mismo endpoint de siempre.
 */
import React, { useState } from 'react';
import { Alert, Button, Modal, Space, Table, Tag, Typography, Upload, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { addItemToOrder, moveOrderActionToClient, readPurchaseOrder, type PurchaseOrderRead } from '@/services/OrderAssemblyService';
import type { OrderItemUI } from './order-types';
import { currency } from './order-helpers';

interface Props {
  fkProjectActionId?: number | null;
  orderId?: number | null;
  result: PurchaseOrderRead | null;
  onResult: (r: PurchaseOrderRead) => void;
  onItemsAdded: (items: OrderItemUI[]) => void;
  disabled?: boolean;
}

export default function PurchaseOrderImport({ fkProjectActionId, orderId, result, onResult, onItemsAdded, disabled }: Props) {
  const [reading, setReading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const [added, setAdded] = useState(false);

  const ready = (result?.lines || []).filter((l) => l.reference && l.extension);
  const blocked = (result?.lines || []).length - ready.length;

  const read = async (file: File) => {
    if (!fkProjectActionId) return message.error('Abre el pedido desde una acción de tipo PEDIDO.');
    setReading(true);
    try {
      const r = await readPurchaseOrder(fkProjectActionId, file);
      onResult(r);
      setAdded(false);
      setOpen(true);
      if (r.client_match && !orderId) offerMove(r, file);
    } catch (e: any) {
      message.error(e?.message || 'No se pudo leer la orden de compra');
    } finally {
      setReading(false);
    }
  };

  // La OC es de otra razón social del grupo: se propone pasar el pedido a ella
  // y se vuelve a leer la misma OC, ya con el cliente correcto.
  const offerMove = (r: PurchaseOrderRead, file: File) => {
    const target = r.client_match!;
    Modal.confirm({
      title: `La orden se factura a ${r.header.bill_to_name}`,
      content: `¿Pasar este pedido a ${target.name} (NIT ${target.nit}, sucursal ${target.branch})?`,
      okText: 'Pasar el pedido',
      cancelText: 'Dejarlo aquí',
      onOk: async () => {
        try {
          await moveOrderActionToClient(fkProjectActionId as number, target.id);
          message.success(`El pedido pasó a ${target.name}`);
          await read(file);
        } catch (e: any) {
          message.error(e?.data?.message || e?.message || 'No se pudo cambiar el cliente');
        }
      },
    });
  };

  const addLines = async () => {
    if (!orderId || !result) return;
    setAdding(true);
    const items: OrderItemUI[] = [];
    try {
      for (const l of ready) {
        const payload = {
          fk_order_to_create: orderId,
          reference: l.reference as string,
          description: l.siesa_description || l.description,
          short_reference: l.client_reference,
          extension: l.extension as string,
          warehouse: result.defaults.warehouse,
          price_list: result.defaults.price_list,
          measure_unit: l.siesa_measure_unit || 'UND',
          quantity: l.quantity,
          unit_price: l.unit_price,
          notes: l.comment || undefined,
        };
        const res: any = await addItemToOrder(payload as any);
        if (!res?.id) throw new Error(`Línea ${l.line} (${l.client_reference}): ${res?.message || 'no se pudo agregar'}`);
        items.push({ ...payload, key: String(res.id), total: l.quantity * l.unit_price } as OrderItemUI);
      }
      setAdded(true);
      message.success(`${items.length} líneas agregadas al pedido`);
    } catch (e: any) {
      message.error(e?.data?.message || e?.message || 'Error agregando las líneas');
    } finally {
      if (items.length) onItemsAdded(items);
      setAdding(false);
    }
  };

  const columns = [
    { title: '#', dataIndex: 'line', width: 44 },
    { title: 'Ref. cliente', dataIndex: 'client_reference', width: 130 },
    { title: 'Cant.', dataIndex: 'quantity', width: 60, align: 'right' as const },
    { title: 'Precio', dataIndex: 'unit_price', width: 90, align: 'right' as const, render: (v: number) => currency(v) },
    {
      title: 'Ref. SIESA',
      dataIndex: 'reference',
      width: 110,
      render: (v: string | null, l: PurchaseOrderRead['lines'][number]) =>
        v ? (
          <Space direction="vertical" size={0}>
            {v}
            {l.reference_source && <Typography.Text type="secondary" style={{ fontSize: 11 }}>{l.reference_source}</Typography.Text>}
          </Space>
        ) : '—',
    },
    { title: 'Talla', dataIndex: 'extension', width: 70, render: (v: string | null) => v || '—' },
    {
      title: 'Revisar',
      key: 'check',
      render: (_: unknown, l: PurchaseOrderRead['lines'][number]) => (
        <Space direction="vertical" size={0}>
          {l.problems.map((p) => <Typography.Text key={p} type="danger">{p}</Typography.Text>)}
          {l.warnings.map((w) => <Typography.Text key={w} type="warning">{w}</Typography.Text>)}
          {l.comment && <Typography.Text type="secondary">{l.comment}</Typography.Text>}
          {!l.problems.length && !l.warnings.length && !l.comment && <Tag color="green">Lista</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 12 }}>
      <Space wrap>
        <Upload accept="application/pdf" showUploadList={false} beforeUpload={(f) => { read(f); return false; }} disabled={disabled || reading}>
          <Button icon={<FilePdfOutlined />} loading={reading} disabled={disabled}>
            {result ? 'Subir otra orden de compra' : 'Subir orden de compra (PDF)'}
          </Button>
        </Upload>
        {result && (
          <Button type="link" onClick={() => setOpen(true)}>
            OC {result.header.order}: {ready.length} líneas listas{blocked ? `, ${blocked} por revisar` : ''}
          </Button>
        )}
        {result && orderId && !added && (
          <Button type="primary" loading={adding} disabled={disabled || !ready.length || result.problems.length > 0} onClick={addLines}>
            Agregar {ready.length} líneas de la OC
          </Button>
        )}
      </Space>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={<Button onClick={() => setOpen(false)}>Cerrar</Button>}
        width={960}
        title={result ? `Orden de compra ${result.header.order} · ${result.header.bill_to_name}` : 'Orden de compra'}
      >
        {result && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              Fecha {result.header.order_date}
              {result.header.campaign ? ` · Campaña ${result.header.campaign}` : ''}
              {result.totals ? ` · ${result.totals.quantity} unidades · ${currency(result.totals.value)} con IVA` : ''}
            </Typography.Text>
            {result.problems.map((p) => <Alert key={p} type="error" showIcon message={p} />)}
            {result.warnings.map((w) => <Alert key={w} type="warning" showIcon message={w} />)}
            {!orderId && (
              <Alert
                type="info"
                showIcon
                message="Crea el pedido con estos datos y en el paso de artículos agrega las líneas de la OC."
              />
            )}
            <Table rowKey="line" size="small" pagination={false} scroll={{ y: 420 }} columns={columns} dataSource={result.lines} />
          </Space>
        )}
      </Modal>
    </div>
  );
}
